import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../..';
import { LoadStatus } from '../../../model';
import { createAppThunk } from '../../thunks';
import {
    deleteConversationStatus,
    fetchConversationStatuses,
    saveConversationStatus,
} from './conversationStatusesAPI';
import { ConversationStatusEntity } from '../../../model/ConversationStatusEntity';

interface ConversationStatusesState {
    items: ConversationStatusEntity[];
    loading: LoadStatus;
}

const initialState: ConversationStatusesState = { items: [], loading: 'loading' };

export const loadConversationStatuses = createAppThunk(
    'conversationStatuses/load',
    fetchConversationStatuses
);

export const upsertConversationStatus = createAppThunk<
    ConversationStatusEntity,
    Pick<ConversationStatusEntity, 'rowKey' | 'title' | 'colour' | 'statusPriority' | 'isDefault' | 'isInProgress' | 'isAwaitingResponse' | 'pauseHandlingTime' | 'isAutoGenerated'>
>('conversationStatuses/upsert', saveConversationStatus);

export const removeConversationStatus = createAppThunk<string, string>(
    'conversationStatuses/remove',
    async (rowKey) => {
        await deleteConversationStatus(rowKey);
        return rowKey;
    }
);

const conversationStatusesSlice = createSlice({
    name: 'conversationStatuses',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(loadConversationStatuses.pending, (state) => { state.loading = 'loading'; })
            .addCase(loadConversationStatuses.fulfilled, (state, action: PayloadAction<ConversationStatusEntity[]>) => {
                state.loading = 'idle';
                state.items = action.payload;
            })
            .addCase(loadConversationStatuses.rejected, (state) => { state.loading = 'failed'; })
            .addCase(upsertConversationStatus.fulfilled, (state, action) => {
                state.loading = 'idle';
                const index = state.items.findIndex((x) => x.rowKey === action.payload.rowKey);
                if (index >= 0) state.items[index] = action.payload;
                else state.items.push(action.payload);
            })
            .addCase(upsertConversationStatus.rejected, (state) => { state.loading = 'failed'; })
            .addCase(removeConversationStatus.fulfilled, (state, action) => {
                state.loading = 'idle';
                state.items = state.items.filter((x) => x.rowKey !== action.payload);
            })
            .addCase(removeConversationStatus.rejected, (state) => { state.loading = 'failed'; });
    },
});

export const selectConversationStatuses = (state: RootState) => state.conversationStatuses;
export default conversationStatusesSlice.reducer;
